'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface ProductSpec {
  label: string;
  value: string;
}

interface Product {
  id: string;
  name: string;
  subtitle: string;
  capacity: string;
  tier: string;
  tierColor: string;
  desc: string;
  tags: string[];
  warranty: string;
  img: string;
  alt: string;
  specs: ProductSpec[];
}

interface ProductCategory {
  heading: string;
  subheading: string;
  products: Product[];
}

interface TabData {
  label: string;
  range: string;
  icon: string;
  categories: ProductCategory[];
}

const tabs: TabData[] = [
{
  label: 'Under 5 kW',
  range: 'UPS, Inverter & Small Office',
  icon: '🔋',
  categories: [
  {
    heading: 'LFP Battery Packs',
    subheading: 'Direct drop-in replacements for SMF/lead-acid batteries in UPS and inverter systems.',
    products: [
    {
      id: 'lfp-100-12',
      name: 'AGLS LFP-100/12.8V',
      subtitle: '1.28 kWh LiFePO₄ Battery Bank',
      capacity: '1.28 kWh',
      tier: 'Entry Level',
      tierColor: 'badge-blue',
      desc: 'Compact entry-level lithium battery bank ideal for small UPS systems and basic office backup needs.',
      tags: ['Wall-Mounted', 'Smart BMS', 'LED Indicators', 'Compact'],
      warranty: '5 Years',
      img: "https://img.rocket.new/generatedImages/rocket_gen_img_1549ac0a4-1778658399672.png",
      alt: 'Compact 12.8V 100Ah LFP battery pack for UPS systems',
      specs: [
      { label: 'Nominal Voltage', value: '12.8V' },
      { label: 'Capacity', value: '100Ah / 1.28 kWh' },
      { label: 'Cell Chemistry', value: 'LiFePO₄ (LFP)' },
      { label: 'Cycle Life', value: '2000+ cycles @ 80% DoD' },
      { label: 'Max Charge Current', value: '50A (0.5C)' },
      { label: 'Max Discharge Current', value: '100A (1C)' },
      { label: 'Operating Temp', value: '-20°C to +60°C' },
      { label: 'BMS Protection', value: 'OVP, UVP, OCP, OTP, SCP' },
      { label: 'Communication', value: 'RS485 / Bluetooth' },
      { label: 'Dimensions', value: '260 × 168 × 215 mm' },
      { label: 'Weight', value: '12.5 kg' },
      { label: 'Warranty', value: '5 Years' }]

    },
    {
      id: 'lfp-100-25',
      name: 'AGLS LFP-100/25.6V',
      subtitle: '2.56 kWh LiFePO₄ Battery Bank',
      capacity: '2.56 kWh',
      tier: 'Small Office',
      tierColor: 'badge-green',
      desc: 'Standard battery bank for small office solar installations and inverter backup systems.',
      tags: ['Floor-Standing', 'Smart BMS', 'Parallel Ready', 'LCD Display'],
      warranty: '5 Years',
      img: "https://img.rocket.new/generatedImages/rocket_gen_img_1b95c79e9-1776540708300.png",
      alt: '25.6V 100Ah LFP battery bank for solar and inverter backup',
      specs: [
      { label: 'Nominal Voltage', value: '25.6V' },
      { label: 'Capacity', value: '100Ah / 2.56 kWh' },
      { label: 'Cell Chemistry', value: 'LiFePO₄ (LFP)' },
      { label: 'Cycle Life', value: '2000+ cycles @ 80% DoD' },
      { label: 'Max Charge Current', value: '50A (0.5C)' },
      { label: 'Max Discharge Current', value: '100A (1C)' },
      { label: 'Parallel Strings', value: 'Up to 4 in parallel' },
      { label: 'Operating Temp', value: '-20°C to +60°C' },
      { label: 'BMS Protection', value: 'OVP, UVP, OCP, OTP, SCP' },
      { label: 'Communication', value: 'RS485 / CAN / Bluetooth' },
      { label: 'Weight', value: '23 kg' },
      { label: 'Warranty', value: '5 Years' }]

    },
    {
      id: 'lfp-200-12',
      name: 'AGLS LFP-200/12.8V',
      subtitle: '2.56 kWh High-Capacity LFP Bank',
      capacity: '2.56 kWh',
      tier: 'Small Office',
      tierColor: 'badge-green',
      desc: 'High-capacity 12V battery bank for extended backup times and demanding small office applications.',
      tags: ['200Ah Capacity', 'Smart BMS', 'Deep Cycle', 'Long Life'],
      warranty: '5 Years',
      img: "https://img.rocket.new/generatedImages/rocket_gen_img_16e8282ac-1778658398891.png",
      alt: '12.8V 200Ah high capacity LFP battery pack for extended backup',
      specs: [
      { label: 'Nominal Voltage', value: '12.8V' },
      { label: 'Capacity', value: '200Ah / 2.56 kWh' },
      { label: 'Cell Chemistry', value: 'LiFePO₄ (LFP)' },
      { label: 'Cycle Life', value: '3000+ cycles @ 80% DoD' },
      { label: 'Max Charge Current', value: '100A (0.5C)' },
      { label: 'Max Discharge Current', value: '200A (1C)' },
      { label: 'Operating Temp', value: '-20°C to +60°C' },
      { label: 'BMS Protection', value: 'OVP, UVP, OCP, OTP, SCP, BVP' },
      { label: 'Communication', value: 'RS485 / Bluetooth' },
      { label: 'Weight', value: '24 kg' },
      { label: 'Warranty', value: '5 Years' }]

    }]

  },
  {
    heading: 'All-in-One ESS (Inverter + Battery)',
    subheading: 'Integrated energy storage systems with built-in inverter and lithium battery.',
    products: [
    {
      id: 'ess-1350',
      name: 'AGLS ESS-1350',
      subtitle: '1200VA In-Built Lithium ESS',
      capacity: '1.28 kWh',
      tier: 'Small Office',
      tierColor: 'badge-blue',
      desc: 'Compact wall-mountable all-in-one energy storage system with built-in inverter and lithium battery.',
      tags: ['Wall-Mountable', 'LCD Display', 'Bypass Switch', 'Pure Sine Wave'],
      warranty: '3 Years',
      img: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee5d8ef3-1767979410350.png",
      alt: 'Wall-mounted all-in-one ESS unit with integrated inverter and lithium battery',
      specs: [
      { label: 'Inverter Power', value: '1200VA / 960W' },
      { label: 'Battery Capacity', value: '1.28 kWh (LFP)' },
      { label: 'Input Voltage', value: '100–280V AC' },
      { label: 'Output Waveform', value: 'Pure Sine Wave' },
      { label: 'Transfer Time', value: '<10ms' },
      { label: 'Efficiency', value: '>92%' },
      { label: 'Cycle Life', value: '2000+ cycles' },
      { label: 'Display', value: 'LCD with status indicators' },
      { label: 'Mounting', value: 'Wall-mount / Floor-stand' },
      { label: 'Warranty', value: '3 Years' }]

    },
    {
      id: 'ess-2500',
      name: 'AGLS ESS-2500',
      subtitle: '2500VA In-Built Lithium ESS',
      capacity: '2.56 kWh',
      tier: 'Commercial',
      tierColor: 'badge-orange',
      desc: 'Mid-range all-in-one ESS designed for small commercial spaces, offices, and shops with higher power requirements.',
      tags: ['Wall-Mountable', 'LCD Display', 'IoT & App', 'Intelligent BMS'],
      warranty: '3 Years',
      img: "https://img.rocket.new/generatedImages/rocket_gen_img_149552dcc-1778658399147.png",
      alt: 'Commercial 2500VA ESS with IoT connectivity and intelligent BMS',
      specs: [
      { label: 'Inverter Power', value: '2500VA / 2000W' },
      { label: 'Battery Capacity', value: '2.56 kWh (LFP)' },
      { label: 'Input Voltage', value: '100–280V AC' },
      { label: 'Output Waveform', value: 'Pure Sine Wave' },
      { label: 'Transfer Time', value: '<10ms' },
      { label: 'Efficiency', value: '>93%' },
      { label: 'IoT', value: 'Wi-Fi + App monitoring' },
      { label: 'Cycle Life', value: '2000+ cycles' },
      { label: 'Warranty', value: '3 Years' }]

    }]

  }]

},
{
  label: '5 kW – 50 kW',
  range: 'Industrial & Commercial',
  icon: '⚡',
  categories: [
  {
    heading: 'Industrial BESS Systems',
    subheading: 'Scalable lithium energy storage for factories, commercial buildings, and solar integration.',
    products: [
    {
      id: 'bess-10kw',
      name: 'AGLS BESS-10K',
      subtitle: '10 kWh Industrial Lithium BESS',
      capacity: '10 kWh',
      tier: 'Industrial',
      tierColor: 'badge-orange',
      desc: 'Rack-mounted 10 kWh BESS for industrial UPS, solar storage, and DG replacement applications.',
      tags: ['Rack-Mount', 'CAN Bus', 'MPPT Ready', 'Modular'],
      warranty: '5 Years',
      img: "https://img.rocket.new/generatedImages/rocket_gen_img_1f7df1aef-1778658398892.png",
      alt: 'Industrial 10kWh rack-mounted BESS system for factory backup power',
      specs: [
      { label: 'Capacity', value: '10 kWh (LFP)' },
      { label: 'Nominal Voltage', value: '48V / 51.2V' },
      { label: 'Max Charge Power', value: '5 kW' },
      { label: 'Max Discharge Power', value: '10 kW' },
      { label: 'Cycle Life', value: '4000+ cycles @ 80% DoD' },
      { label: 'Communication', value: 'CAN 2.0 / RS485 / Modbus' },
      { label: 'Mounting', value: '4U Rack / Floor Cabinet' },
      { label: 'IP Rating', value: 'IP55' },
      { label: 'Operating Temp', value: '-10°C to +55°C' },
      { label: 'Warranty', value: '5 Years' }]

    },
    {
      id: 'bess-30kw',
      name: 'AGLS BESS-30K',
      subtitle: '30 kWh Commercial BESS',
      capacity: '30 kWh',
      tier: 'Commercial Plus',
      tierColor: 'badge-orange',
      desc: 'High-capacity commercial BESS for large offices, manufacturing plants, and solar-plus-storage projects.',
      tags: ['Cabinet Design', 'BMS + EMS', 'Grid-Interactive', 'Remote Monitor'],
      warranty: '5 Years',
      img: "https://img.rocket.new/generatedImages/rocket_gen_img_131376abe-1778658399356.png",
      alt: '30kWh commercial BESS cabinet for large-scale energy storage',
      specs: [
      { label: 'Capacity', value: '30 kWh (LFP)' },
      { label: 'Nominal Voltage', value: '153.6V (3S)' },
      { label: 'Max Charge Power', value: '15 kW' },
      { label: 'Max Discharge Power', value: '30 kW' },
      { label: 'Cycle Life', value: '4000+ cycles @ 80% DoD' },
      { label: 'Communication', value: 'CAN / RS485 / Ethernet / 4G' },
      { label: 'Mounting', value: 'Floor Cabinet (IP55)' },
      { label: 'Cooling', value: 'Active Air Cooling' },
      { label: 'Warranty', value: '5 Years' }]

    }]

  }]

},
{
  label: '50 kW – 250 kW',
  range: 'Utility & Large Commercial',
  icon: '🏭',
  categories: [
  {
    heading: 'Utility-Scale BESS',
    subheading: 'Large-scale containerized energy storage for grid support, peak shaving, and renewable integration.',
    products: [
    {
      id: 'bess-100kw',
      name: 'AGLS BESS-100K',
      subtitle: '100 kWh Containerized BESS',
      capacity: '100 kWh',
      tier: 'Utility',
      tierColor: 'badge-orange',
      desc: 'Containerized 100 kWh BESS for peak shaving, demand response, and large commercial solar storage.',
      tags: ['Containerized', 'EMS Included', 'Grid-Tie', 'SCADA Ready'],
      warranty: '5 Years',
      img: "https://img.rocket.new/generatedImages/rocket_gen_img_1b61590af-1778658399363.png",
      alt: '100kWh containerized utility-scale BESS for grid support and peak shaving',
      specs: [
      { label: 'Capacity', value: '100 kWh (LFP)' },
      { label: 'System Voltage', value: '307.2V' },
      { label: 'Max Charge Power', value: '50 kW' },
      { label: 'Max Discharge Power', value: '100 kW' },
      { label: 'Cycle Life', value: '6000+ cycles @ 80% DoD' },
      { label: 'Communication', value: 'Modbus TCP / SCADA / 4G LTE' },
      { label: 'Enclosure', value: '20ft Container / IP65' },
      { label: 'Cooling', value: 'Precision Air Conditioning' },
      { label: 'Fire Safety', value: 'Integrated FM200 System' },
      { label: 'Warranty', value: '5 Years' }]

    },
    {
      id: 'bess-250kw',
      name: 'AGLS BESS-250K',
      subtitle: '250 kWh Large-Scale BESS',
      capacity: '250 kWh',
      tier: 'Enterprise',
      tierColor: 'badge-orange',
      desc: 'Enterprise-grade 250 kWh BESS for industrial campuses, data centers, and utility-scale renewable projects.',
      tags: ['Modular Design', 'AI-Optimized EMS', 'Grid-Forming', 'Remote O&M'],
      warranty: '5 Years',
      img: "https://img.rocket.new/generatedImages/rocket_gen_img_121552600-1778658399729.png",
      alt: '250kWh enterprise BESS for industrial campuses and utility-scale projects',
      specs: [
      { label: 'Capacity', value: '250 kWh (LFP)' },
      { label: 'System Voltage', value: '614.4V' },
      { label: 'Max Charge Power', value: '125 kW' },
      { label: 'Max Discharge Power', value: '250 kW' },
      { label: 'Cycle Life', value: '6000+ cycles @ 80% DoD' },
      { label: 'Communication', value: 'Modbus TCP / DNP3 / IEC 61850' },
      { label: 'Enclosure', value: '40ft Container / IP65' },
      { label: 'Cooling', value: 'Precision Air Conditioning' },
      { label: 'Fire Safety', value: 'Integrated FM200 + Detection' },
      { label: 'Warranty', value: '5 Years' }]

    }]

  }]

}];


function ProductCard({ product }: {product: Product;}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card-dark overflow-hidden">
      <div className="flex flex-col md:flex-row">
        {/* Image */}
        <div className="md:w-48 flex-shrink-0">
          <img
            src={product.img}
            alt={product.alt}
            className="w-full h-48 md:h-full object-cover" />
          
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`badge ${product.tierColor}`}>{product.tier}</span>
                <span className="flex items-center gap-1 text-primary text-sm font-semibold">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {product.capacity}
                </span>
              </div>
              <h3 className="text-slate-900 font-bold text-lg">{product.name}</h3>
              <p className="text-slate-500 text-sm">{product.subtitle}</p>
            </div>
          </div>

          <p className="text-slate-600 text-sm mb-4">{product.desc}</p>

          <div className="flex flex-wrap gap-2 mb-4">
            {product.tags.map((tag) =>
            <span key={tag} className="text-xs bg-slate-100 border border-slate-200 text-slate-600 px-2 py-1 rounded-lg">
                {tag}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mb-4">
            <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span className="text-slate-600 text-sm">{product.warranty} warranty</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setExpanded(!expanded)}
              className="btn-outline text-sm">
              
              {expanded ? 'Hide Specifications' : 'View Full Specifications'}
              <svg
                className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <a
              href={"https://wa.me/919737701071?text=Hi%2C%20I'm%20interested%20in%20the%20" + encodeURIComponent(product.name)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm">
              
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Enquire Now
            </a>
          </div>
        </div>
      </div>

      {/* Expandable Specs */}
      <div className={`accordion-content ${expanded ? 'open' : ''}`}>
        <div className="border-t border-slate-200 p-6">
          <h4 className="text-slate-900 font-semibold mb-4 flex items-center gap-2">
            <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Full Technical Specifications
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {product.specs.map((spec) =>
            <div key={spec.label} className="spec-row">
                <span className="text-slate-500">{spec.label}</span>
                <span className="text-slate-900 font-medium">{spec.value}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BessEssPage() {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const elements = document.querySelectorAll('.animate-on-scroll');
    const observer = new IntersectionObserver(
      (entries) => {entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add('visible');});},
      { threshold: 0.1 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const currentTab = tabs[activeTab];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 circuit-bg opacity-30" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(14,165,233,0.06) 0%, transparent 70%)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="section-label mb-6">BESS / ESS</div>
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">
            Battery Energy Storage
            <br />
            <span className="gradient-text-green">Systems</span>
          </h1>
          <p className="text-slate-600 text-lg max-w-3xl leading-relaxed">
            From compact battery packs under 1 kWh for UPS and inverter backup, to scalable systems up to 250 kW
            for off-grid solar, DG replacement, and commercial energy storage. All with intelligent BMS,
            zero maintenance, and up to 6000+ cycle life.
          </p>
        </div>
      </section>

      {/* Lead-Acid vs Lithium */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-10 animate-on-scroll">
            Why Switch from{' '}
            <span className="text-red-600">Lead-Acid</span> to{' '}
            <span className="gradient-text-green">Lithium</span>?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Lead-Acid */}
            <div className="card-dark p-6 border-red-500/20 animate-on-scroll">
              <h3 className="text-red-600 font-bold text-lg mb-4">❌ SMF / Lead-Acid Batteries</h3>
              <ul className="space-y-3">
                {[
                'Die every 2–3 years (300 cycles)',
                'Heavy, take up huge floor space',
                'Fail without warning, acid leaks',
                'Need regular water topping and checks',
                'No protection against overcharging'].
                map((item) =>
                <li key={item} className="flex items-center gap-3 text-slate-600 text-sm">
                    <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs flex-shrink-0">✕</span>
                    {item}
                  </li>
                )}
              </ul>
            </div>
            {/* Lithium */}
            <div className="card-dark p-6 border-primary/20 animate-on-scroll" style={{ animationDelay: '0.1s' }}>
              <h3 className="text-primary font-bold text-lg mb-4">✅ AGILAS Lithium (LFP)</h3>
              <ul className="space-y-3">
                {[
                '2000+ to 6000+ cycles (8–10 year lifespan)',
                '50% smaller, 60% lighter',
                'Smart BMS alerts before any issue',
                'Fit and forget, zero maintenance',
                'Multi-layer BMS protection built in'].
                map((item) =>
                <li key={item} className="flex items-center gap-3 text-slate-600 text-sm">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs flex-shrink-0">✓</span>
                    {item}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Product Tabs */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab buttons */}
          <div className="flex flex-wrap gap-3 mb-10">
            {tabs.map((tab, i) =>
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              className={`tab-btn ${activeTab === i ? 'active' : ''}`}>
              
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            )}
          </div>

          {/* Tab content */}
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                {currentTab.label} — {currentTab.range}
              </h2>
            </div>

            {currentTab.categories.map((cat, ci) =>
            <div key={ci} className="mb-12">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <h3 className="text-slate-900 font-bold text-xl">{cat.heading}</h3>
                </div>
                <p className="text-slate-500 text-sm mb-6 ml-8">{cat.subheading}</p>
                <div className="space-y-6">
                  {cat.products.map((product) =>
                <ProductCard key={product.id} product={product} />
                )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose AGILAS BESS */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why Choose AGILAS BESS?</h2>
            <p className="text-slate-600">Designed as direct replacements for standard SMF and lead-acid battery banks. No wiring changes needed.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
            { icon: '🛡️', title: 'Up to 60 Months Warranty', desc: 'Industry-leading warranty backed by rigorous quality control on every pack' },
            { icon: '🔌', title: 'Drop-in Replacement', desc: 'Fits existing UPS and inverter setups with no wiring changes required' },
            { icon: '🧠', title: 'Intelligent BMS Protection', desc: 'Over-voltage, under-voltage, overcurrent, and thermal protection built in' },
            { icon: '♻️', title: '3x to 10x Lifespan', desc: '2000+ to 6000+ cycle life vs 300 cycles on lead-acid batteries' }].
            map((item, i) =>
            <div key={i} className="card-dark p-6 text-center animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="text-slate-900 font-bold mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 text-center animate-on-scroll">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Need Help Choosing the Right Product?</h2>
          <p className="text-slate-600 mb-8">Our energy experts are here to help you select the perfect energy storage solution.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="https://wa.me/919737701071"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-base px-8 py-3">
              
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Chat with Expert
            </a>
            <Link href="/bms-software" className="btn-secondary text-base px-8 py-3">
              BMS Selection Guide
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>);

}